import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import { Grid } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import ImageViewer from "@/components/general/imageViewer";
import RoundedContentBox from "@/components/general/roundedContentBox";
import LmsBackButton from "@/components/lms/lmsBackButton";
import LmsBookNowModal from "@/components/lms/lmsBookNowModal";
import LmsCourseOverviewHeader from "@/components/lms/lmsCourseOverviewHeader";
import { getLmsCourseFeaturedImage } from "@/components/lms/lms.utils";
import { RootState } from "@/components/state/store";
import { getImageUrl } from "@/helper/imageUrlHandler";
import LmsCourseOverviewReadOnly from "@/routes/lms/lmsCourseOverviewReadOnly";
import styles from "@/routes/lms/lmsCourseOverview.module.css";

export default function LmsMarketingCourseOverview() {
  const { t } = useTranslation();
  const subdomain = window.location.hostname.split(".")[0];
  const { courseId } = useParams<{ courseId: string }>();
  const selectedCompany = useSelector((state: RootState) => state.parent);
  const [course, setCourse] = useState<any>(null);
  const [bookNowOpen, setBookNowOpen] = useState(false);
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const featuredImage = getLmsCourseFeaturedImage(course);
  const featuredImageUrl = featuredImage?.viewLink
    ? getImageUrl(
        featuredImage.viewLink,
        selectedCompany.subjectId
          ? String(selectedCompany.subjectId)
          : undefined
      )
    : null;
  const featuredImageViewerImages = useMemo(
    () =>
      featuredImage?.viewLink && courseId
        ? [
            {
              id: featuredImage.id || courseId,
              name: featuredImage.displayName,
              view_link: featuredImage.viewLink,
              download_link: featuredImage.viewLink,
              extension: featuredImage.displayName.split(".").pop() || "png",
            },
          ]
        : [],
    [courseId, featuredImage]
  );

  return (
    <>
      <Grid container className={styles.mainGrid}>
        <Grid item xs={12}>
          <div className={styles.courseOverviewNav}>
            <LmsBackButton />
          </div>
          <RoundedContentBox>
            <LmsCourseOverviewHeader
              featuredImageUrl={featuredImageUrl}
              imageAlt={
                featuredImage?.displayName ||
                course?.title ||
                course?.name ||
                t("course_featured_image")
              }
              title={course?.title ?? course?.name ?? t("courses")}
              onViewImage={() => setImageViewerOpen(true)}
              canViewImage={Boolean(featuredImage?.viewLink)}
              topRightContent={
                <GenericButton
                  onClick={() => setBookNowOpen(true)}
                  startIcon={<ShoppingCartOutlinedIcon />}
                  className={styles.headerActionButton}
                  data-testid="lms-marketing-book-now-detail-button"
                >
                  {t("lms_marketing_book_now")}
                </GenericButton>
              }
            />
          </RoundedContentBox>
        </Grid>
        <Grid item xs={12}>
          <LmsCourseOverviewReadOnly
            requiredMode="manager"
            subdomain="manage"
            renderOnlyOverview
            onCourseLoaded={setCourse}
          />
        </Grid>
      </Grid>

      <LmsBookNowModal
        open={bookNowOpen}
        onClose={() => setBookNowOpen(false)}
      />

      {featuredImageViewerImages.length > 0 && (
        <ImageViewer
          open={imageViewerOpen}
          onClose={() => setImageViewerOpen(false)}
          images={featuredImageViewerImages}
          initialImageIndex={0}
          doNotShowFileName={subdomain !== "admin"}
        />
      )}
    </>
  );
}
